import { useState, useEffect, useCallback } from 'react';
import { StoreProductsService } from '../services/store-products';

/**
 * Hook personalizado para gerenciar produtos da loja
 * @param {string} connectorUid - UID do connector
 * @returns {Object} Estado e funções para gerenciar produtos
 */
export const useStoreProducts = (connectorUid) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectorInfo, setConnectorInfo] = useState(null);

  // Buscar informações do connector
  const fetchConnectorInfo = useCallback(() => {
    if (connectorUid) {
      try {
        const connector = StoreProductsService.getConnectorInfo(connectorUid);
        setConnectorInfo(connector);
      } catch (err) {
        console.error('Erro ao buscar informações do connector:', err);
        setError('Erro ao carregar informações do connector');
      }
    }
  }, [connectorUid]);

  // Buscar produtos
  const fetchProducts = useCallback(async () => {
    if (!connectorUid) return;

    setLoading(true);
    setError(null);

    try {
      const productsData = await StoreProductsService.getProductsByConnector(connectorUid);
      
      // Processar produtos para o formato esperado
      const processedProducts = productsData.map(product => ({
        uid: product.uid,
        name: product.name,
        unique_name: product.unique_name,
        currency: product.currency || 'BRL',
        amount: product.amount,
        formatted_amount: StoreProductsService.formatCurrency(product.amount, product.currency),
        created_at: product.created_at,
        formatted_date: StoreProductsService.formatDate(product.created_at),
        raw_data: product, // Manter dados originais
      }));

      setProducts(processedProducts);
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setError('Erro ao carregar produtos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [connectorUid]);

  // Excluir produto
  const deleteProduct = useCallback(async (productUid) => {
    try {
      await StoreProductsService.deleteProduct(productUid);
      // Atualizar lista após exclusão
      await fetchProducts();
      return { success: true, message: 'Produto excluído com sucesso' };
    } catch (err) {
      console.error('Erro ao excluir produto:', err);
      return { success: false, message: 'Erro ao excluir produto' };
    }
  }, [fetchProducts]);

  // Excluir múltiplos produtos
  const deleteMultipleProducts = useCallback(async (productUids) => {
    try {
      const deletePromises = productUids.map(uid => StoreProductsService.deleteProduct(uid));
      await Promise.all(deletePromises);
      
      // Atualizar lista após exclusão
      await fetchProducts();
      return { success: true, message: 'Produtos excluídos com sucesso' };
    } catch (err) {
      console.error('Erro ao excluir produtos:', err);
      return { success: false, message: 'Erro ao excluir produtos' };
    }
  }, [fetchProducts]);

  // Buscar produto específico
  const getProduct = useCallback(async (productUid) => {
    try {
      const product = await StoreProductsService.getProductByUid(productUid);
      return { success: true, data: product };
    } catch (err) {
      console.error('Erro ao buscar produto:', err);
      return { success: false, message: 'Erro ao carregar produto' };
    }
  }, []);

  // Recarregar dados
  const refresh = useCallback(() => {
    fetchConnectorInfo();
    fetchProducts();
  }, [fetchConnectorInfo, fetchProducts]);

  // Filtrar produtos por texto
  const filterProducts = useCallback((searchText) => {
    if (!searchText) return products;

    const normalizedSearch = searchText.toLowerCase().trim();
    return products.filter(product => 
      product.name.toLowerCase().includes(normalizedSearch) ||
      product.unique_name.toLowerCase().includes(normalizedSearch) ||
      product.currency.toLowerCase().includes(normalizedSearch)
    );
  }, [products]);

  // Inicializar dados
  useEffect(() => {
    fetchConnectorInfo();
    fetchProducts();
  }, [fetchConnectorInfo, fetchProducts]);

  return {
    // Estado
    products,
    loading,
    error,
    connectorInfo,
    
    // Funções
    fetchProducts,
    deleteProduct,
    deleteMultipleProducts,
    getProduct,
    refresh,
    filterProducts,
    
    // Utilitários
    formatCurrency: StoreProductsService.formatCurrency,
    formatDate: StoreProductsService.formatDate,
  };
};
